import type { ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Stagger helper: seconds to delay the reveal. */
  delay?: number;
  /** Render as a different element (default div). */
  as?: "div" | "li" | "section";
};

/**
 * Scroll-triggered reveal used across the marketing pages. Fades content up once
 * as it enters the viewport. When the user prefers reduced motion it renders the
 * content immediately with no transform, so nothing important depends on motion.
 */
export default function Reveal({ children, className, delay = 0, as = "div" }: RevealProps) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as];

  if (reduce) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionTag>
  );
}
